import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getStoreHealthCheck } from '@/api/clarity'
import { ScalrTableColumn } from '@/components/customComponents/leaderboard/Leaderboard'
import {
  StoreHealthMetricModel,
  StoreHealthModel,
  StoreHealthTempMetricModel,
} from '@/types/clarity'
import { dateToString } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'

const storeHealthCheckColumns: ScalrTableColumn[] = [
  { header: 'Branch', accessorKey: 'branch', type: 'text' },
  {
    header: '% Change',
    accessorKey: 'percentageTrend',
    type: 'percentage',
  },
  {
    header: '$ Impact',
    accessorKey: 'contributionTrend',
    type: 'money',
  },
  // { header: 'Percentage', accessorKey: 'percentage', type: 'percentage' },
  // { header: 'Contribution', accessorKey: 'contribution', type: 'money' },
]

export default function usePnlStoreHealthCheck(
  startDate: string,
  endDate: string,
  dateFilterType: DateType
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [healthData, setHealthData] = useState<StoreHealthModel | null>(null)
  const [healthTrendData, setHealthTrendData] =
    useState<StoreHealthModel | null>(null)
  const [mergedHealthData, setMergedHealthData] =
    useState<StoreHealthModel | null>(null)

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { healthMetrics } = await fetchTempStoreHealthCheck(
          startDate,
          endDate
        )

        setHealthData(healthMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { healthMetrics } = await fetchTempStoreHealthCheck(
          dateToString(trendingDate, 'from', 'yyyy-mm'),
          dateToString(trendingDate, 'to', 'yyyy-mm')
        )

        setHealthTrendData(healthMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate, dateFilterType])

  // Merge data function
  const mergeHealthData = useCallback(
    (
      currentData: StoreHealthModel,
      trendData: StoreHealthModel
    ): StoreHealthModel => {
      const mergeMetrics = (
        current: StoreHealthTempMetricModel[],
        trend: StoreHealthTempMetricModel[]
      ): StoreHealthMetricModel[] => {
        return current?.map((currentMetric, index) => {
          const trendMetric = trend.find(
            (t) => t.branch === currentMetric.branch
          )

          return {
            branch: currentMetric.branch,
            percentage: currentMetric.percentage,
            contribution: currentMetric.contribution,
            percentageTrend: trendMetric
              ? currentMetric.percentage - trendMetric.percentage
              : currentMetric.percentage,
            contributionTrend: trendMetric
              ? currentMetric.contribution - trendMetric.contribution
              : currentMetric.contribution,
          }
        })
      }

      return {
        netSales: mergeMetrics(currentData.netSales, trendData.netSales),
        cogs: mergeMetrics(currentData.cogs, trendData.cogs),
        storeLabor: mergeMetrics(currentData.storeLabor, trendData.storeLabor),
        opEx: mergeMetrics(currentData.opEx, trendData.opEx),
        ebitda: mergeMetrics(currentData.ebitda, trendData.ebitda),
      }
    },
    []
  )

  // Effect to merge data when both are available
  useEffect(() => {
    if (healthData && healthTrendData) {
      const merged = mergeHealthData(healthData, healthTrendData)
      setMergedHealthData(merged)
    }
  }, [healthData, healthTrendData, mergeHealthData])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching P&L store health check data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    healthData: mergedHealthData,
    storeHealthCheckColumns,
    loading,
    error,
    refetch,
  }
}

async function fetchTempStoreHealthCheck(
  startDate: string,
  endDate: string
): Promise<{
  healthMetrics: StoreHealthModel
}> {
  try {
    const response = await getStoreHealthCheck(startDate, endDate)

    const healthMetrics: StoreHealthModel = {
      netSales: response.net_sales,
      cogs: response.cogs,
      storeLabor: response.store_labor,
      opEx: response.op_ex,
      ebitda: response.ebitda,
    }

    return {
      healthMetrics,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}
